// Wire protocol. Inputs up at 60 Hz with redundancy; snapshots down at 30 Hz,
// quantized; events on the reliable channel.

import { INPUT_REDUNDANCY, PROTOCOL_VERSION } from './core';
import { Reader, Writer, GameEvent, readEvent, writeEvent } from './codec';
import { InputFrame, PLAYER_FIELDS, PlayerState, activeWeaponId, barrelSide } from './player';
import { elIndex } from './elements';
import { Enemy } from './enemies';
import type { Game } from './game';
import { RunSnap, decRun, encRun } from './runwire';

export const enum C2S { Hello = 1, Inputs = 2, Ping = 3, SetClass = 4, SetLoadout = 5, Mark = 6, Ready = 20, Sit = 21, SetLook = 30, SetContract = 31, /** wildcard draft: pick offer slot 0..2, or 255 to pass (sim/cards.ts) */ PickCard = 32 }
export const enum S2C { Welcome = 1, Snapshot = 2, Events = 3, Pong = 4, Reject = 5 }

// ------------------------------------------------------------------ client → server
export function encHello(w: Writer, name: string, cls: number, token: string) {
  w.reset(); w.u8w(C2S.Hello); w.u8w(PROTOCOL_VERSION); w.strw(name); w.u8w(cls); w.strw(token);
  return w.bytes();
}

/** Newest first; the server picks out the ones it hasn't seen. */
export function encInputs(w: Writer, frames: InputFrame[]) {
  w.reset(); w.u8w(C2S.Inputs);
  const n = Math.min(frames.length, INPUT_REDUNDANCY);
  w.u32w(frames[0].tick); w.u8w(n);
  for (let i = 0; i < n; i++) {
    const f = frames[i];
    w.unitw(f.mx); w.unitw(f.my); w.angw(f.yaw); w.i16w(Math.round(f.pitch * 10000)); w.angw(f.myaw); w.u32w(f.buttons);
    w.f32w(f.viewTick);
  }
  return w.bytes();
}
export function decInputs(r: Reader): InputFrame[] {
  const tick = r.u32(), n = r.u8();
  const out: InputFrame[] = [];
  for (let i = 0; i < n; i++) {
    out.push({ tick: tick - i, mx: r.unit(), my: r.unit(), yaw: r.ang(), pitch: r.i16() / 10000, myaw: r.ang(), buttons: r.u32(), viewTick: r.f32() });
  }
  return out;
}

function quantAng(v: number) {
  const a = Math.round(((v % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / (Math.PI * 2) * 65535);
  let r = (a / 65535) * Math.PI * 2;
  if (r > Math.PI) r -= Math.PI * 2;
  return r;
}

/** Quantize a frame exactly as the wire will, so prediction uses what the server sees. */
export function quantizeInput(f: InputFrame) {
  f.mx = Math.max(-127, Math.min(127, Math.round(f.mx * 127))) / 127;
  f.my = Math.max(-127, Math.min(127, Math.round(f.my * 127))) / 127;
  f.yaw = quantAng(f.yaw);
  f.myaw = quantAng(f.myaw);
  f.pitch = Math.round(f.pitch * 10000) / 10000;
  return f;
}

export function encPing(w: Writer, t: number) { w.reset(); w.u8w(C2S.Ping); w.f64w(t); return w.bytes(); }
export function encSetClass(w: Writer, cls: number) { w.reset(); w.u8w(C2S.SetClass); w.u8w(cls); return w.bytes(); }
export function encSetLoadout(w: Writer, primary: number, secondary: number, grenade: number) {
  w.reset(); w.u8w(C2S.SetLoadout); w.u8w(primary); w.u8w(secondary); w.u8w(grenade); return w.bytes();
}
/** A ping or quick-chat line (MarkKind) at a world point, optionally on a robot. */
export function encMark(w: Writer, kind: number, x: number, y: number, z: number, eid: number) {
  w.reset(); w.u8w(C2S.Mark); w.u8w(kind); w.f32w(x); w.f32w(y); w.f32w(z); w.u16w(eid); return w.bytes();
}
/** saloon: saddle up (1) or not yet (0) */
export function encReady(w: Writer, on: boolean) { w.reset(); w.u8w(C2S.Ready); w.u8w(on ? 1 : 0); return w.bytes(); }
/** saloon: sit on SEATS[seat], or stand (NO_SEAT) */
export function encSit(w: Writer, seat: number) { w.reset(); w.u8w(C2S.Sit); w.u8w(seat); return w.bytes(); }

/** Service Record look (sim/progression.ts): gun finish, earned-contract mask, worn title. Cosmetic + roster only. */
export function encSetLook(w: Writer, finish: number, contracts: number, title: string) {
  w.reset(); w.u8w(C2S.SetLook); w.u8w(finish); w.u8w(contracts); w.strw(title.slice(0, 40)); return w.bytes();
}

/** Sign a contract for the posse's next ride (255 = tear it up). Saloon only; the room checks someone riding earned it. */
export function encPickCard(w: Writer, slot: number, wslot = 255) {
  w.reset(); w.u8w(C2S.PickCard); w.u8w(slot); w.u8w(wslot); return w.bytes();
}
export function encSetContract(w: Writer, contract: number) {
  w.reset(); w.u8w(C2S.SetContract); w.u8w(contract); return w.bytes();
}

// ------------------------------------------------------------------ server → client
export interface Welcome { pid: number; seed: number; tick: number; token: string; slot: number; island: number }
export function encWelcome(w: Writer, m: Welcome) {
  w.reset(); w.u8w(S2C.Welcome); w.u8w(m.pid); w.u32w(m.seed); w.u32w(m.tick); w.strw(m.token); w.u8w(m.slot); w.u16w(m.island);
  return w.bytes();
}
export function decWelcome(r: Reader): Welcome {
  const m = { pid: r.u8(), seed: r.u32(), tick: r.u32(), token: r.str(), slot: r.u8(), island: 0 };
  if (r.left >= 2) m.island = r.u16();
  return m;
}

export function encPong(w: Writer, clientT: number, serverTick: number) {
  w.reset(); w.u8w(S2C.Pong); w.f64w(clientT); w.f64w(serverTick); return w.bytes();
}

export function encEvents(w: Writer, tick: number, evs: GameEvent[]) {
  w.reset(); w.u8w(S2C.Events); w.u32w(tick); w.u16w(evs.length);
  for (const e of evs) writeEvent(w, e);
  return w.bytes();
}
export function decEvents(r: Reader) {
  const tick = r.u32(), n = r.u16();
  const evs: GameEvent[] = [];
  for (let i = 0; i < n; i++) evs.push(readEvent(r));
  return { tick, evs };
}

export interface RemotePlayer {
  id: number; cls: number; slot: number; phase: number; move: number;
  x: number; y: number; z: number; vx: number; vy: number; vz: number; yaw: number; pitch: number;
  hp: number; shield: number; heat: number; skyT: number; flags: number; reviveFrac: number; chainN: number;
  /** weapon in hand (sim/weapons.ts id) */
  weapon: number;
  /** movement detail for animation: MFlag bits | dodgeK << 4 | landK << 6 */
  mflags: number;
  /** class energy blade: bit 0 drawn, bits 1-2 the flourish step; bit 3 = chugging whiskey */
  blade: number;
  /** the elements on a cowboy (sim/elements.ts): bit 0 burning, bit 1 acid-softened, bit 2 wet; bit 3 the overshield is up */
  flags2: number;
}
export const enum MFlag { Aim = 1, Sprint = 2, Barrel = 4, Stall = 8 }
export const enum PFlag { Grounded = 1, OnFire = 2, Paused = 4, Cloak = 8, Reload = 16, Melee = 32, Chain = 64, Revive = 128 }

export interface NetEnemy {
  id: number; type: number; st: number; prog: number;
  x: number; y: number; z: number; yaw: number;
  hp: number; shield: number; flags: number; primedBy: number; target: number;
  aimX: number; aimY: number; aimZ: number;
  /** EFlag2 bits */
  flags2: number;
  /** ROSTER.md: affix bits 0-2, cloak 8, big 16, rout 32, redshell 64 (EExt) */
  ext: number;
  /** the parts shot off a living robot (defs.ts Part bits), playtest 3 */
  parts: number;
}
export const enum EFlag { Primed = 1, Hyper = 2, Stun = 4, Burn = 8, Vuln = 16, Strip = 32, Airborne = 64, Nemesis = 128 }
/** the second robot flags byte (playtest 1): slowed by ice or a Time grenade, reeling from a stagger (WEIGHT.md) */
export const enum EFlag2 { Slow = 1, Stagger = 2, /** sim/elements.ts */ Frozen = 4, Wet = 8, /** bits 4-6: the element the robot carries (ELEMS index + 1, 0 none) */ ElShift = 4, /** bit 7: a Shooter / Sniper with its gun shot off, or the Engineer's small turret */ Disarmed = 128 }
/** NetEnemy.ext (ROSTER.md): the affix in bits 0-2 (defs.ts Affix), then the flags */
export const enum EExt { AffixMask = 7, Cloak = 8, Big = 16, Rout = 32, Redshell = 64, /** a gold bounty robot (CHASE.md) */ Gold = 128 }

export interface Snapshot {
  tick: number;
  ack: number; // last input tick applied for you
  cushion: number; // how many ticks early your inputs are arriving
  frozen: boolean;
  lives: number;
  dirState: number; wave: number; tier: number; dirTimer: number;
  me: Record<string, number> | null;
  players: RemotePlayer[];
  enemies: NetEnemy[];
  /** islands, phase, boss parts, livestock (sim/runwire.ts); null from an encoder without a game */
  run: RunSnap | null;
}

const F64_FIELDS = new Set<string>(['x', 'y', 'z', 'vx', 'vy', 'vz', 'hoverY', 'heat', 'hp', 'shield']);

export function encSnapshot(
  w: Writer, tick: number, ack: number, cushion: number, frozen: boolean, lives: number,
  dir: { state: number; wave: number; tier: number; timer: number },
  me: PlayerState | undefined, players: PlayerState[], enemies: Enemy[], game?: Game,
) {
  w.reset(); w.u8w(S2C.Snapshot);
  w.u32w(tick); w.u32w(ack); w.i8w(Math.max(-127, Math.min(127, Math.round(cushion)))); w.u8w(frozen ? 1 : 0); w.u8w(lives);
  w.u8w(dir.state); w.u8w(dir.wave); w.u8w(dir.tier); w.u16w(Math.max(0, Math.min(65535, dir.timer)));
  if (me) {
    w.u8w(1);
    // your own full state at the acked tick, for reconciliation. Position and velocity go as
    // float64 so the replay starts from exactly the server's numbers (float32 at 100 m is ~8 um,
    // enough to flip a ground-contact test); the rest are small counters and fit float32 exactly.
    for (const f of PLAYER_FIELDS) { if (F64_FIELDS.has(f)) w.f64w(me[f]); else w.f32w(me[f]); }
  } else w.u8w(0);
  w.u8w(players.length);
  for (const p of players) {
    w.u8w(p.id); w.u8w(p.cls); w.u8w(p.slot); w.u8w(p.phase); w.u8w(p.move);
    w.cmw(p.x); w.cmw(p.y); w.cmw(p.z); w.cmw(p.vx); w.cmw(p.vy); w.cmw(p.vz);
    w.angw(p.yaw); w.i16w(Math.round(p.pitch * 10000));
    w.u8w(Math.round(p.hp)); w.u8w(Math.round(Math.min(255, p.shield))); w.u8w(Math.round(Math.min(255, p.heat)));
    w.u8w(Math.round(p.skyT * 255));
    const flags = (p.grounded ? PFlag.Grounded : 0) | (p.onFire ? PFlag.OnFire : 0) | (p.paused ? PFlag.Paused : 0)
      | (p.cloakT > 0 ? PFlag.Cloak : 0) | (p.reloadT > 0 ? PFlag.Reload : 0) | (p.meleeT > 0 ? PFlag.Melee : 0)
      | (p.chainT > 0 ? PFlag.Chain : 0) | (p.reviveT > 0 ? PFlag.Revive : 0);
    w.u8w(flags);
    w.u8w(Math.round(Math.min(1, p.reviveT / 120) * 255));
    w.u8w(Math.min(255, p.chainN));
    w.u8w(activeWeaponId(p) & 255);
    w.u8w((p.aimOn ? MFlag.Aim : 0) | (p.sprint || p.slide ? MFlag.Sprint : 0) | (p.move === 3 && p.dodgeT > 0 ? MFlag.Barrel : 0) | (p.stallT > 0 ? MFlag.Stall : 0)
      // in a barrel roll the dodge kind is known, so its two bits carry the juke side instead (2 right, 3 left)
      | ((p.move === 3 && p.dodgeT > 0 ? (barrelSide(p) < 0 ? 3 : 2) : p.dodgeK & 3) << 4) | ((p.landK & 3) << 6));
    // bit 3: mid-chug (sim/whiskey.ts), so everyone sees the bottle come out
    w.u8w((p.blade ? 1 : 0) | ((p.combo & 3) << 1) | (p.drinkT > 0 ? 8 : 0) | (p.timeT > 0 ? 16 : 0)); // bit 4: caught in a Time grenade
    w.u8w((p.burnT > 0 ? 1 : 0) | (p.acidT > 0 ? 2 : 0) | (p.wetT > 0 ? 4 : 0) | (p.over > 0 ? 8 : 0)); // bit 3: the overshield is up (the gold skin)
  }
  w.u16w(enemies.length);
  for (const e of enemies) {
    w.u16w(e.id); w.u8w(e.type); w.u8w(e.st);
    w.u8w(Math.round(Math.min(1, e.stDur > 0 ? e.stT / e.stDur : 0) * 255));
    w.cmw(e.x); w.cmw(e.y); w.cmw(e.z); w.angw(e.yaw);
    w.u8w(Math.round(Math.max(0, e.hp / e.maxHp) * 255));
    w.u8w(e.maxShield > 0 ? Math.round(Math.max(0, e.shield / e.maxShield) * 255) : 0);
    const f = (e.primedBy >= 0 ? EFlag.Primed : 0) | (e.hyper ? EFlag.Hyper : 0) | (e.stunT > 0 ? EFlag.Stun : 0)
      | (e.burnT > 0 ? EFlag.Burn : 0) | (e.vulnT > 0 ? EFlag.Vuln : 0) | (e.stripT > 0 ? EFlag.Strip : 0) | (e.airborne ? EFlag.Airborne : 0) | (e.nemesis ? EFlag.Nemesis : 0);
    w.u8w(f);
    w.u8w((e.slowT > 0 || e.iceSlow > 0 ? EFlag2.Slow : 0) | (e.staggerT > 0 ? EFlag2.Stagger : 0) | (e.frozenT > 0 ? EFlag2.Frozen : 0) | (e.wetT > 0 ? EFlag2.Wet : 0) | ((elIndex(e.armorEl) + 1) << EFlag2.ElShift) | (((e.type === 1 || e.type === 3) && e.gunHp <= 0) || e.type === 19 ? EFlag2.Disarmed : 0));
    // the roster lane (ROSTER.md): bits 0-2 the affix, 3 cloaked (a Stalker between strikes), 4 big (the Swarm Captain), 5 routing, 6 redshell up
    w.u8w((e.affix & 7) | (e.type === 18 && e.revealT === 0 ? EExt.Cloak : 0) | (e.big ? EExt.Big : 0) | (e.routT > 0 ? EExt.Rout : 0) | (e.redshell ? EExt.Redshell : 0) | (e.gold ? EExt.Gold : 0));
    w.u8w(e.parts & 15); // the parts shot off (defs.ts Part bits), playtest 3
    w.u8w(e.primedBy < 0 ? 255 : e.primedBy);
    w.u8w(e.target < 0 ? 255 : e.target);
    // telegraph aim point (sniper laser, bomber target) only matters in windup
    if (e.st === 1) { w.u8w(1); w.cmw(e.aimX); w.cmw(e.aimY); w.cmw(e.aimZ); } else w.u8w(0);
  }
  if (game) encRun(w, game);
  return w.bytes();
}

export function decSnapshot(r: Reader): Snapshot {
  const s: Snapshot = {
    tick: r.u32(), ack: r.u32(), cushion: r.i8(), frozen: r.u8() === 1, lives: r.u8(),
    dirState: r.u8(), wave: r.u8(), tier: r.u8(), dirTimer: r.u16(), me: null, players: [], enemies: [], run: null,
  };
  if (r.u8()) {
    const me: Record<string, number> = {};
    for (const f of PLAYER_FIELDS) me[f] = F64_FIELDS.has(f) ? r.f64() : r.f32();
    s.me = me;
  }
  const np = r.u8();
  for (let i = 0; i < np; i++) {
    const p: RemotePlayer = {
      id: r.u8(), cls: r.u8(), slot: r.u8(), phase: r.u8(), move: r.u8(),
      x: r.cm(), y: r.cm(), z: r.cm(), vx: r.cm(), vy: r.cm(), vz: r.cm(), yaw: r.ang(), pitch: r.i16() / 10000,
      hp: r.u8(), shield: r.u8(), heat: r.u8(), skyT: r.u8() / 255, flags: r.u8(), reviveFrac: r.u8() / 255, chainN: r.u8(), weapon: r.u8(), mflags: r.u8(), blade: r.u8(), flags2: r.u8(),
    };
    s.players.push(p);
  }
  const ne = r.u16();
  for (let i = 0; i < ne; i++) {
    const e: NetEnemy = {
      id: r.u16(), type: r.u8(), st: r.u8(), prog: r.u8() / 255,
      x: r.cm(), y: r.cm(), z: r.cm(), yaw: r.ang(), hp: r.u8() / 255, shield: r.u8() / 255, flags: r.u8(),
      flags2: r.u8(), ext: r.u8(), parts: r.u8(), primedBy: r.u8(), target: r.u8(), aimX: 0, aimY: 0, aimZ: 0,
    };
    if (r.u8()) { e.aimX = r.cm(); e.aimY = r.cm(); e.aimZ = r.cm(); }
    s.enemies.push(e);
  }
  if (r.left > 0) s.run = decRun(r);
  return s;
}
